import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import ReactModal from 'react-modal'
import styled from 'styled-components'

import { RESTART_AUTOMATION } from 'data/layoutcreator/mutations/automation'
import { GET_AUTOMATION_STATUS } from 'data/layoutcreator/queries/automation'
import { GET_PROJECT_TEMPLATES } from 'data/layoutcreator/queries/projects'
import IslandModal from 'shared/components/modals/IslandModal'
import { AutomationEntities, CoverTypes } from 'shared/types/global'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Switch from 'shared/ui/Switch'
import Text from 'shared/ui/Text'

import EntitiesSelector from './components/EntitiesSelector'
import { EntityOption } from './types'

export const CancelButton = styled.a`
  margin-top: 86px;
  color: ${({ theme }) => theme.color.base.c5};
  text-decoration: underline;
  cursor: pointer;
`

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  margin-top: 4px;
  color: ${({ theme }) => theme.color.base.c6};
  margin-bottom: 36px;
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 60px;
`

const ENTITIES = [
  AutomationEntities.quotes,
  AutomationEntities.profiles,
  AutomationEntities.collages,
  AutomationEntities.rankings,
  AutomationEntities.reports
]

interface NewPageModalProps extends ReactModal.Props {
  closeModal: () => void
  projectId: string
  groupId: string
  coverType: CoverTypes
}

const RestartModal = ({
  isOpen,
  closeModal,
  projectId,
  groupId,
  coverType
}: NewPageModalProps) => {
  const { formatMessage } = useIntl()

  const [selectedEntities, setSelectedEntities] = useState<
    EntityOption[]
  >(['all'])
  const [removeExistingPages, setRemoveExistingPages] =
    useState<boolean>(false)

  const [restartAutomation] = useMutation(RESTART_AUTOMATION)

  const handleRestart = () => {
    restartAutomation({
      variables: {
        groupId,
        projectId,
        coverType,
        entities: selectedEntities.includes('all')
          ? ENTITIES
          : selectedEntities,
        removeExistingPages
      },
      refetchQueries: [
        { query: GET_AUTOMATION_STATUS, variables: { projectId } }
      ]
    })
    closeModal()
  }

  return (
    <IslandModal isOpen={isOpen} closeModal={closeModal}>
      <Title>{formatMessage({ id: 'Add.page.title' })}</Title>
      <Description>
        Select the entities you want to include in the new automation
        run
      </Description>

      <Flex direction="column" rowGap={32} alignItems="flex-end">
        <EntitiesSelector
          selectedEntities={selectedEntities}
          onSelectedEntitiesChange={setSelectedEntities}
        />

        <Flex alignItems="center" columnGap={16}>
          <Text
            weight={500}
            size={14}
            text="Remove existing pages of these entities"
          />
          <Switch
            value={removeExistingPages}
            onChange={setRemoveExistingPages}
          />
        </Flex>
      </Flex>

      <Footer>
        <Button
          intent="secondary-base"
          textId="Add.page.cancel"
          onPress={closeModal}
          size="sm"
        />

        <Button
          intent="primary-brand-02"
          textId="Add.page.restart"
          onPress={handleRestart}
          size="sm"
        />
      </Footer>
    </IslandModal>
  )
}

export default RestartModal
